import { useState } from "react";
import { MessageSquare, Flag, FileDown, Trash2, Search } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ConfirmModal } from "@/components/admin/ConfirmModal";
import { getMessages, getMessageThread } from "@/lib/admin/queries";
import { flagMessage, deleteMessage } from "@/lib/admin/actions";
import { useAuth } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

function formatRelative(dateStr: string): string {
  const d = new Date(dateStr);
  const now = new Date();
  const diff = (now.getTime() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString("en-CA");
}

function getInitials(name?: string): string {
  if (!name) return "?";
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

export default function Messages() {
  const { profile } = useAuth();
  const actorId = profile?.id ?? "";
  const qc = useQueryClient();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [buildingFilter, setBuildingFilter] = useState("all");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  const messagesQuery = useQuery({
    queryKey: ["admin", "messages", buildingFilter, unreadOnly],
    queryFn: () =>
      getMessages({
        buildingId: buildingFilter !== "all" ? buildingFilter : undefined,
      }),
  });

  const threadQuery = useQuery({
    queryKey: ["admin", "message-thread", selectedId],
    queryFn: () => getMessageThread(selectedId!),
    enabled: !!selectedId,
  });

  const allMessages: Record<string, unknown>[] = messagesQuery.data?.data ?? [];

  // Filter by search
  const filtered = allMessages.filter((msg) => {
    const sender = msg.sender as Record<string, unknown> | undefined;
    const senderName = ((sender?.full_name as string) ?? "").toLowerCase();
    const preview = ((msg.body as string) ?? "").toLowerCase();
    const q = search.toLowerCase();
    if (q && !senderName.includes(q) && !preview.includes(q)) return false;
    return true;
  });

  const selectedThread = threadQuery.data?.data;

  const flagMutation = useMutation({
    mutationFn: (id: string) => flagMessage(id, actorId),
    onSuccess: () => {
      toast.success("Thread flagged for review");
      qc.invalidateQueries({ queryKey: ["admin", "messages"] });
    },
    onError: () => toast.error("Failed to flag"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteMessage(id, actorId),
    onSuccess: () => {
      toast.success("Message deleted");
      qc.invalidateQueries({ queryKey: ["admin", "messages"] });
      setDeleteTarget(null);
      if (selectedId === deleteTarget) setSelectedId(null);
    },
    onError: () => toast.error("Failed to delete"),
  });

  return (
    <div className="p-6 space-y-4">
      <h1 className="text-2xl font-bold text-gray-900">Messages</h1>

      <div className="flex h-[calc(100vh-220px)] min-h-[500px] border border-gray-200 rounded-lg overflow-hidden bg-white">
        {/* Left Panel — Thread List */}
        <div className="w-1/3 border-r border-gray-200 flex flex-col min-w-0">
          {/* Filters */}
          <div className="p-3 border-b border-gray-100 space-y-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search messages…"
                className="pl-8 h-8 text-sm"
              />
            </div>
            <div className="flex items-center gap-2">
              <Select value={buildingFilter} onValueChange={setBuildingFilter}>
                <SelectTrigger className="h-7 text-xs flex-1">
                  <SelectValue placeholder="All buildings" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All buildings</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant={unreadOnly ? "default" : "outline"}
                size="sm"
                className="h-7 text-xs shrink-0 whitespace-nowrap"
                onClick={() => setUnreadOnly((v) => !v)}
              >
                Unread
              </Button>
            </div>
          </div>

          {/* Thread list */}
          <div className="flex-1 overflow-y-auto">
            {messagesQuery.isLoading ? (
              <div className="p-3 space-y-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <Skeleton className="h-9 w-9 rounded-full shrink-0" />
                    <div className="flex-1 space-y-1">
                      <Skeleton className="h-3 w-24" />
                      <Skeleton className="h-3 w-full" />
                    </div>
                  </div>
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center p-6">
                <MessageSquare className="h-8 w-8 text-gray-300 mb-2" />
                <p className="text-sm text-gray-400">No messages found</p>
              </div>
            ) : (
              filtered.map((msg) => {
                const isSelected = selectedId === (msg.id as string);
                const sender = msg.sender as Record<string, unknown> | undefined;
                const initials = getInitials(sender?.full_name as string | undefined);

                return (
                  <button
                    key={msg.id as string}
                    className={cn(
                      "w-full text-left px-3 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors",
                      isSelected && "bg-blue-50 border-blue-100"
                    )}
                    onClick={() => setSelectedId(msg.id as string)}
                  >
                    <div className="flex items-start gap-2">
                      <div className="h-9 w-9 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-xs font-semibold shrink-0">
                        {initials}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-1">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {(sender?.full_name as string) ?? "Unknown"}
                          </p>
                          <span className="text-[10px] text-gray-400 shrink-0 whitespace-nowrap">
                            {msg.created_at ? formatRelative(msg.created_at as string) : ""}
                          </span>
                        </div>
                        <p className="text-xs text-gray-500 truncate">
                          {(msg.organization_name as string) ?? (msg.building_name as string) ?? "No building"}
                        </p>
                        <p className="text-xs text-gray-400 truncate max-w-[180px] mt-0.5">
                          {(msg.subject as string) ?? (msg.body as string) ?? "—"}
                        </p>
                      </div>
                      {/* Unread dot placeholder */}
                      {!msg.read && (
                        <div className="h-2 w-2 rounded-full bg-blue-500 shrink-0 mt-1" />
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Right Panel — Thread Viewer */}
        <div className="flex-1 flex flex-col min-w-0">
          {!selectedId ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
              <MessageSquare className="h-12 w-12 text-gray-200 mb-3" />
              <p className="text-base font-medium text-gray-400">Select a conversation</p>
              <p className="text-sm text-gray-300 mt-1">Choose a thread from the left to view it here</p>
            </div>
          ) : (
            <>
              {/* Thread header */}
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                {threadQuery.isLoading ? (
                  <div className="space-y-1">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-48" />
                  </div>
                ) : selectedThread ? (
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">
                      {(selectedThread as Record<string, unknown> & { sender?: Record<string, unknown> }).sender?.full_name as string ?? "Unknown"}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-gray-500 mt-0.5">
                      <span>{(selectedThread as Record<string, unknown>).organization_name as string ?? "No building"}</span>
                      {(selectedThread as Record<string, unknown>).unit_number && (
                        <>
                          <span>·</span>
                          <span>Unit {(selectedThread as Record<string, unknown>).unit_number as string}</span>
                        </>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-400">Thread not found</p>
                )}
              </div>

              {/* Messages area */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {threadQuery.isLoading ? (
                  <div className="space-y-3">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <div key={i} className={cn("flex", i % 2 === 0 ? "justify-start" : "justify-end")}>
                        <div className="max-w-[70%] space-y-1">
                          <Skeleton className="h-3 w-16" />
                          <Skeleton className="h-12 w-full rounded-lg" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : selectedThread ? (
                  // Single message view (real thread would have multiple messages)
                  <div className="flex justify-start">
                    <div className="max-w-[75%]">
                      <p className="text-xs text-gray-400 mb-1 ml-1">
                        {(selectedThread as Record<string, unknown> & { sender?: Record<string, unknown> }).sender?.full_name as string ?? "Unknown"} ·{" "}
                        {(selectedThread as Record<string, unknown>).created_at
                          ? new Date((selectedThread as Record<string, unknown>).created_at as string).toLocaleString("en-CA")
                          : ""}
                      </p>
                      <div className="bg-gray-100 rounded-lg rounded-tl-none px-3 py-2">
                        <p className="text-sm text-gray-800 whitespace-pre-wrap">
                          {(selectedThread as Record<string, unknown>).body as string ??
                            (selectedThread as Record<string, unknown>).subject as string ??
                            (selectedThread as Record<string, unknown>).message as string ??
                            "No content"}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <p className="text-sm text-gray-400">No messages in this thread</p>
                  </div>
                )}
              </div>

              {/* Admin action bar */}
              <div className="px-4 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 gap-1.5 text-xs whitespace-nowrap"
                    onClick={() => flagMutation.mutate(selectedId)}
                    disabled={flagMutation.isPending}
                  >
                    <Flag className="h-3.5 w-3.5" />
                    Flag thread
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 gap-1.5 text-xs whitespace-nowrap"
                    onClick={() => {
                      toast.info("PDF export coming soon");
                    }}
                  >
                    <FileDown className="h-3.5 w-3.5" />
                    Export PDF
                  </Button>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 gap-1.5 text-xs text-red-600 border-red-200 hover:bg-red-50 whitespace-nowrap"
                  onClick={() => setDeleteTarget(selectedId)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Delete message
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      <ConfirmModal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (deleteTarget) deleteMutation.mutate(deleteTarget);
        }}
        title="Delete Message"
        description="Are you sure you want to permanently delete this message? This cannot be undone."
        requireTyping
        loading={deleteMutation.isPending}
        variant="destructive"
      />
    </div>
  );
}
